// @flow

import React, {useState, useCallback, useEffect} from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';
import OptionsList from './OptionsList';
import SavedOptionsModal from './SavedOptionsModal';

import type {ProductOptionSetting} from '../../../../../entities/product';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {
  StoreOptionProvider,
  useStoreSelector,
  useStoreOptionActions,
} from './OptionsContext';
import {Add, ArrowForward} from '@material-ui/icons';
import {Link} from 'react-router-dom';
import {useProductOptionSetting} from '../../../hooks/useProductOptionSetting';
import FeatureAccessOverlay from '../../../../entitlement/components/feature-access-overlay';
import TemporaryFeatureAlert from '../../../../entitlement/components/TemporaryFeatureAlert';

type Props = {
  productId?: string,
  onChange: (optionSetting: ProductOptionSetting) => void,
};

const useStyles = makeStyles(theme => ({
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    borderRadius: '.75rem',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: '.5rem',
    background: 'white',
    position: 'relative',

    gap: '.75rem',

    [theme.breakpoints.up('md')]: {
      padding: '1rem',
    },
  },
  loadingContent: {
    background: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    borderRadius: '.75rem',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: '3rem 1rem',
    gap: '1.5rem',
    textAlign: 'center',
  },
  loadingIconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: colors.lightPurple,
  },
  emptyContent: {
    background: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    borderRadius: '.75rem',
    padding: '3rem 1rem',
    gap: '1.5rem',
    textAlign: 'center',
  },
  emptyIconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: colors.lightPurple,
    marginBottom: theme.spacing(1),
  },
  emptyTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: colors.grey800 || '#2d3748',
    marginBottom: theme.spacing(0.5),
  },
  emptyDescription: {
    color: colors.grey600,
    fontSize: '0.95rem',
    lineHeight: 1.5,
    maxWidth: 400,
    marginBottom: theme.spacing(1),
  },
  loadingText: {
    color: colors.grey700 || '#4a5568',
    fontSize: '1rem',
    fontWeight: '500',
  },
  createButton: {
    padding: '.5rem 2rem',
    fontWeight: '600',
  },
  title: {
    fontSize: '1rem',
    fontWeight: '600!important',
  },
  subtitle: {
    color: colors.grey600,
    fontSize: 14,
  },
  optionListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  addButton: {
    textTransform: 'none',
    backgroundColor: 'transparent',
    color: colors.grey700,
    border: `1px dashed ${colors.grey700}`,
    fontWeight: 500,
    borderRadius: '1rem',
    padding: '.75rem',
    '&:hover': {
      backgroundColor: colors.grey50,
    },
  },
  upgradeButton: {
    backgroundColor: colors.primary,
    color: 'white',
    fontSize: '0.8125rem',
    fontWeight: 600,
    textTransform: 'none',
    padding: '0.375rem 1.25rem',
    boxShadow: '0 2px 8px rgba(123, 57, 255, 0.2)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: colors.purple4,
      boxShadow: '0 4px 12px rgba(123, 57, 255, 0.3)',
      transform: 'translateY(-1px)',
    },
  },
}));

function OptionsContent(props: Props) {
  const {onChange} = props;
  const classes = useStyles();

  const optionStoreEnabled = useStoreSelector(s => s.enabled);
  const optionStoreGroups = useStoreSelector(s => s.groups);

  const optionStoreGroupIds = useStoreSelector(s => s.groupIds);

  const {setProductOptionEnabled, addNewOptionGroup} = useStoreOptionActions();

  useEffect(() => {
    if (!onChange) return;
    onChange({
      enabled: optionStoreEnabled,
      groups: optionStoreGroups,
    });
  }, [onChange, optionStoreEnabled, optionStoreGroups]);

  const [showSavedOptionsModal, setShowSavedOptionsModal] = useState(false);

  const handleViewSavedOptions = useCallback(() => {
    setShowSavedOptionsModal(true);
  }, []);

  const handleCloseSavedOptionsModal = useCallback(() => {
    setShowSavedOptionsModal(false);
  }, []);

  const handleAddSavedOptions = selectedOptions => {
    addNewOptionGroup(selectedOptions);
  };

  return (
    <div className={classes.contentSection}>
      <FeatureAccessOverlay feature="PRODUCT_OPTIONS" />
      <TemporaryFeatureAlert
        feature="PRODUCT_OPTIONS"
        plans={['TIER_360', 'TIER_350']}
      />
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={optionStoreEnabled}
              onChange={(e, checked) => {
                setProductOptionEnabled(checked);
              }}
              name="enableOptions"
              color="primary"
            />
          }
          classes={{label: classes.title}}
          label="Opciones de producto"
        />
        <Typography className={classes.subtitle}>
          Utiliza esta configuración cuando tu producto se puede customizar.
        </Typography>
      </div>

      {optionStoreEnabled && (
        <>
          <Divider className={classes.divider} />
          <div className={classes.optionListContainer}>
            {optionStoreGroups.length > 0 && (
              <OptionsList optionGroups={optionStoreGroups} />
            )}
            <Box className={classes.actions}>
              <CustomButton
                className={classes.addButton}
                startIcon={<Add />}
                onClick={handleViewSavedOptions}
                fullWidth
              >
                Seleccionar Opciones Guardadas
              </CustomButton>
              <CustomButton
                variant="contained"
                className={classes.upgradeButton}
                component={Link}
                to="/product-option"
                size="small"
                endIcon={<ArrowForward />}
              >
                Crear Opciones
              </CustomButton>
            </Box>
          </div>
        </>
      )}
      {showSavedOptionsModal && (
        <SavedOptionsModal
          open={showSavedOptionsModal}
          onClose={handleCloseSavedOptionsModal}
          onAddOptions={handleAddSavedOptions}
          configuredGroupIds={optionStoreGroupIds}
        />
      )}
    </div>
  );
}

function Options(props: Props) {
  const {productId} = props;
  const {loading, productOptionSetting} = useProductOptionSetting(productId);

  if (loading) {
    return <div>Loading...</div>;
  }

  const initialState = productOptionSetting || {
    enabled: false,
    groups: [],
  };

  return (
    <StoreOptionProvider initial={initialState}>
      <OptionsContent onChange={props.onChange} />
    </StoreOptionProvider>
  );
}

const MemoOptions = React.memo(Options);

export default MemoOptions;
